import { useCollection } from '@cloudscape-design/collection-hooks';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ButtonDropdown, { type ButtonDropdownProps } from '@cloudscape-design/components/button-dropdown';
import ContentLayout from '@cloudscape-design/components/content-layout';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Link from '@cloudscape-design/components/link';
import Modal from '@cloudscape-design/components/modal';
import Pagination from '@cloudscape-design/components/pagination';
import Select, { type SelectProps } from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Env, VpnGatewayRecord, VpnLinkAction, VpnPeerStatus } from '@fleet-cert-console/shared';
import { fetchVpnPeerStatus, listVpnGateways, vpnApprove, vpnInitiate, vpnLink } from '../api/client.js';
import { getConfig } from '../config.js';
import { VpnApproveModal } from '../components/VpnApproveModal.js';
import { VpnConnectHelpModal } from '../components/VpnConnectHelpModal.js';
import { portForwardCommand, sshCommand, VpnGatewayDetail, webUiPortForwardCommand } from '../components/VpnGatewayDetail.js';
import { copyToClipboard } from '../lib/clipboard.js';
import { useSplitPanel } from '../lib/splitPanel.js';
import { TablePreferences, useTablePrefs } from '../lib/tablePrefs.js';
import {
  autostartLabel,
  autostartToggleTarget,
  LINK_ITEM_IDS,
  linkActionItems,
  linkActionOfItem,
  linkIndicator,
  linkSentFlash,
} from '../lib/vpnLink.js';
import { peerOnline, peersByKey, timeAgo } from '../lib/vpnPeers.js';
import { vpnStatusIndicator } from '../lib/vpnStatus.js';
import { customerProjectLabel, gatewayMatches, provisionedLabel } from '../lib/vpnProvisioning.js';

// Environments the console can show. The BFF decides which are actually usable (its VPN env list);
// an env without the control plane answers 400 and the page shows that error.
const ENV_OPTIONS: SelectProps.Option[] = [
  { label: 'Dev', value: 'dev' },
  { label: 'Prod', value: 'prod' },
];
const envFromParam = (value: string | null): Env => (value === 'prod' ? 'prod' : 'dev');

// `hidden` = off by default; users turn it on in the table preferences (useTablePrefs merges new
// columns into stored prefs, so existing users get Link/Autostart on and Client/Firmware off).
const CONTENT_OPTIONS: ReadonlyArray<{ id: string; label: string; alwaysVisible?: boolean; hidden?: boolean }> = [
  { id: 'gatewayId', label: 'Gateway', alwaysVisible: true },
  { id: 'vpnStatus', label: 'Status' },
  { id: 'provisioned', label: 'Provisioned' },
  { id: 'online', label: 'VPN online' },
  { id: 'link', label: 'Link' },
  { id: 'autostart', label: 'Autostart' },
  { id: 'lastSeen', label: 'Last seen' },
  { id: 'vpnIp', label: 'VPN address' },
  { id: 'groupId', label: 'Group' },
  { id: 'customerProject', label: 'Customer / project', hidden: true },
  { id: 'gatewayPubKey', label: 'WireGuard key' },
  { id: 'updatedAt', label: 'Updated' },
  { id: 'client', label: 'Client', hidden: true },
  { id: 'firmware', label: 'Firmware', hidden: true },
];

/** Statuses that mean the control plane is mid-flight — the page polls while any row is in one. */
const ACTIVE_STATUSES = new Set(['requested', 'provisioning', 'applying']);

const POLL_MS = 5000;

/** Mirrors the BFF's gatewayId rule (ids are interpolated into MQTT topics). */
const gatewayIdValid = (id: string): boolean => /^[A-Za-z0-9._:-]{1,128}$/.test(id);

// Module-level on purpose: Cloudscape marks a column "currently sorted" by comparing the comparator
// BY IDENTITY, so an inline arrow re-created each render never matches and the header arrow sticks.
const provisionedComparator = (a: VpnGatewayRecord, b: VpnGatewayRecord) =>
  (a.provisioning?.confirmedAt ?? '').localeCompare(b.provisioning?.confirmedAt ?? '');

type RowState = 'none' | 'notEnrolled' | 'requestedNoKey' | 'requestedWithKey' | 'inFlight' | 'applied' | 'failed';

function rowState(g: VpnGatewayRecord | null): RowState {
  if (!g) return 'none';
  const s = g.vpnStatus;
  if (!s) return 'notEnrolled';
  if (s === 'requested') return g.gatewayPubKey ? 'requestedWithKey' : 'requestedNoKey';
  if (s === 'provisioning' || s === 'applying') return 'inFlight';
  if (s === 'applied') return 'applied';
  if (s === 'failed') return 'failed';
  return 'notEnrolled';
}

export function VpnEnrollmentPage() {
  const navigate = useNavigate();
  // The environment lives in the URL (?env=dev|prod) like the other pages, so links and bookmarks
  // carry it; the default is dev.
  const [searchParams, setSearchParams] = useSearchParams();
  const env = envFromParam(searchParams.get('env'));
  const [items, setItems] = useState<VpnGatewayRecord[]>([]);
  const [selected, setSelected] = useState<VpnGatewayRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  // True when the BFF could not read the fleet index and listed the VPN rows only.
  const [thingsUnavailable, setThingsUnavailable] = useState(false);

  const [initiateOpen, setInitiateOpen] = useState(false);
  const [initiateId, setInitiateId] = useState('');
  const [initiateError, setInitiateError] = useState<string | null>(null);
  const [initiating, setInitiating] = useState(false);

  const [approveOpen, setApproveOpen] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);

  const [reinitiateOpen, setReinitiateOpen] = useState(false);
  const [reinitiateError, setReinitiateError] = useState<string | null>(null);
  const [reinitiating, setReinitiating] = useState(false);

  const [helpOpen, setHelpOpen] = useState(false);

  // Link control (spec 7.3): one in-flight guard for all four actions, plus the Take-VPN-down
  // confirm dialog and its in-dialog error surface.
  const [linkBusy, setLinkBusy] = useState(false);
  const [downOpen, setDownOpen] = useState(false);
  const [downError, setDownError] = useState<string | null>(null);
  // Bumped after a link action so the poll effect re-arms even though vpnStatus (and so
  // `hasActive`) does not change - the gateway's answer lands on the row within ~10 s.
  const [watchNonce, setWatchNonce] = useState(0);

  // Live hub peer state (wg handshakes), fetched on page load and Refresh — an SSM round trip, so
  // deliberately NOT part of the 5 s enrollment poll. null until the first fetch resolves.
  const [peerStatus, setPeerStatus] = useState<{ byKey: Map<string, VpnPeerStatus>; fetchedAt: string; hubInstanceId?: string } | null>(null);

  const { show } = useSplitPanel();

  // Keep polling for a while after an action even if its row hasn't appeared yet.
  const watchUntilRef = useRef(0);
  const [prefs, setPrefs] = useTablePrefs('vpn-gateways', {
    pageSize: 50,
    wrapLines: false,
    contentDisplay: CONTENT_OPTIONS.map((c) => ({ id: c.id, visible: !c.hidden })),
  });

  const load = useCallback(async (opts: { fresh?: boolean; silent?: boolean } = {}) => {
    if (!opts.silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const result = await listVpnGateways(env, { fresh: opts.fresh });
      const next = result.items;
      setItems(next);
      setThingsUnavailable(result.thingsUnavailable === true);
      // Keep the selected row current across any reload (manual Refresh included) — re-point it at
      // the freshly fetched object when the row still exists, clear it only when it's genuinely gone.
      setSelected((prev) => (prev ? (next.find((g) => g.gatewayId === prev.gatewayId) ?? null) : prev));
    } catch (e) {
      setError((e as Error).message);
      watchUntilRef.current = 0; // stop polling on a broken backend rather than spamming it
    } finally {
      if (!opts.silent) setLoading(false);
    }
  }, [env]);

  const loadPeerStatus = useCallback(async () => {
    try {
      const res = await fetchVpnPeerStatus(env);
      setPeerStatus({ byKey: peersByKey(res), fetchedAt: res.fetchedAt, hubInstanceId: res.hubInstanceId });
    } catch {
      // Non-fatal: the hub read failing (no grant yet, hub unreachable) just leaves the live
      // columns at "—"; enrollment state is unaffected.
      setPeerStatus(null);
    }
  }, [env]);

  useEffect(() => {
    void load();
    void loadPeerStatus();
  }, [load, loadPeerStatus]);

  const hasActive = items.some((g) => ACTIVE_STATUSES.has(g.vpnStatus ?? ''));

  useEffect(() => {
    if (!hasActive && Date.now() >= watchUntilRef.current) return;
    const timer = setInterval(() => {
      if (!hasActive && Date.now() >= watchUntilRef.current) return;
      void load({ fresh: true, silent: true });
    }, POLL_MS);
    return () => clearInterval(timer);
    // watchNonce: re-arm after a link action (the row stays `applied`, so hasActive alone would not).
  }, [hasActive, load, watchNonce]);

  const { items: visibleItems, filterProps, filteredItemsCount, collectionProps, paginationProps } = useCollection(items, {
    filtering: { filteringFunction: gatewayMatches },
    sorting: { defaultState: { sortingColumn: { sortingField: 'updatedAt' }, isDescending: true } },
    pagination: { pageSize: prefs.pageSize },
  });

  // Keep the detail panel showing the selected row, refreshed as polling updates it (`selected` gets
  // a new object identity on every silent reload via the setSelected merge above).
  useEffect(() => {
    show(
      selected
        ? {
            header: selected.gatewayId,
            body: (
              <VpnGatewayDetail
                gateway={selected}
                hubInstanceId={peerStatus?.hubInstanceId ?? getConfig().vpnHubInstanceId}
                peer={selected.gatewayPubKey ? peerStatus?.byKey.get(selected.gatewayPubKey) : undefined}
                peerFetchedAt={peerStatus?.fetchedAt}
              />
            ),
          }
        : null,
    );
  }, [selected, peerStatus, show]);

  // Clear the panel on unmount (e.g. navigating away from this page).
  useEffect(() => {
    return () => show(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const state = rowState(selected);

  const openInitiate = () => {
    setInitiateId(selected?.gatewayId ?? '');
    setInitiateError(null);
    setInitiateOpen(true);
  };

  const onInitiate = async () => {
    const id = initiateId.trim();
    if (!gatewayIdValid(id)) {
      setInitiateError('Gateway id may only contain letters, digits, and . _ : - (max 128 chars).');
      return;
    }
    setInitiating(true);
    setInitiateError(null);
    try {
      await vpnInitiate(env, id);
      setInitiateOpen(false);
      setFlash(`Initiate sent to ${id} — the gateway should report its WireGuard key within ~30 s.`);
      watchUntilRef.current = Date.now() + 15 * 60_000;
      await load({ fresh: true, silent: true });
    } catch (e) {
      setInitiateError((e as Error).message);
    } finally {
      setInitiating(false);
    }
  };

  /** Initiate for the already-selected row — used directly for a never-enrolled row, and after
   *  confirmation (key-rotation warning) for an already-enrolled one. `surface` controls where a
   *  failure is shown: 'modal' renders it inside the (open) re-initiate modal, 'page' is used for
   *  the direct "Enroll this gateway" action, which has no modal open to render into. */
  const onReinitiate = async (surface: 'modal' | 'page' = 'modal') => {
    if (!selected) return;
    setReinitiating(true);
    setReinitiateError(null);
    try {
      await vpnInitiate(env, selected.gatewayId);
      setReinitiateOpen(false);
      setFlash(`Initiate sent to ${selected.gatewayId} — the gateway should report its WireGuard key within ~30 s.`);
      watchUntilRef.current = Date.now() + 15 * 60_000;
      await load({ fresh: true, silent: true });
    } catch (e) {
      const message = (e as Error).message;
      if (surface === 'page') setError(message);
      else setReinitiateError(message);
    } finally {
      setReinitiating(false);
    }
  };

  const onApprove = async (approvalReason: string, dryRun: boolean) => {
    if (!selected) return;
    setApproving(true);
    setApproveError(null);
    try {
      await vpnApprove(env, selected.gatewayId, approvalReason, dryRun);
      setApproveOpen(false);
      setFlash(
        dryRun
          ? `Dry-run approve sent for ${selected.gatewayId} — no allocation performed.`
          : `Approve sent for ${selected.gatewayId} — hub provisioning takes ~6 min; the status below narrates it.`,
      );
      watchUntilRef.current = Date.now() + 15 * 60_000;
      await load({ fresh: true, silent: true });
    } catch (e) {
      setApproveError((e as Error).message);
    } finally {
      setApproving(false);
    }
  };

  /** Send one link action for the selected (applied) row. `surface` picks where a failure shows:
   *  'modal' for Take VPN down (its confirm dialog is open), 'page' for the direct menu items. */
  const onLinkAction = async (action: VpnLinkAction, enabled?: boolean, surface: 'modal' | 'page' = 'page') => {
    if (!selected) return;
    setLinkBusy(true);
    if (surface === 'modal') setDownError(null);
    try {
      await vpnLink(env, selected.gatewayId, action, enabled);
      setDownOpen(false);
      setFlash(linkSentFlash(selected.gatewayId));
      // Poll for a minute: the gateway answers within ~10 s and the control plane then writes the
      // vpnLink* / version fields onto the row.
      watchUntilRef.current = Date.now() + 60_000;
      setWatchNonce((n) => n + 1);
      await load({ fresh: true, silent: true });
    } catch (e) {
      const message = (e as Error).message;
      if (surface === 'modal') setDownError(message);
      else setError(message);
    } finally {
      setLinkBusy(false);
    }
  };

  // From the live peer status (the BFF resolves the current hub per env); config.json is a fallback only.
  const hubInstanceId = peerStatus?.hubInstanceId ?? getConfig().vpnHubInstanceId;

  const connectItems: ButtonDropdownProps.ItemOrGroup[] = [
    { id: 'open-terminal', text: 'Open terminal' },
    { id: 'copy-ssh', text: 'Copy SSH command' },
    ...(hubInstanceId ? [{ id: 'copy-portforward', text: 'Copy SSM port-forward command' }] : []),
    ...(hubInstanceId ? [{ id: 'copy-webui-portforward', text: 'Copy web-UI port-forward command' }] : []),
  ];
  const connectDisabledReason =
    state === 'inFlight' ? 'Enrollment in flight' : state === 'none' ? 'Select a gateway' : state === 'applied' ? undefined : 'Available once the gateway is applied';

  const onConnectItemClick = ({ detail }: { detail: ButtonDropdownProps.ItemClickDetails }) => {
    if (!selected) return;
    if (detail.id === 'open-terminal') {
      window.open(
        `/vpn/${encodeURIComponent(selected.gatewayId)}/terminal?env=${env}`,
        // Unique name per click: every Open terminal is a NEW window/session — a reused name
        // renavigates (and kills) a live session, and parallel sessions are supported.
        `vx-terminal-${selected.gatewayId}-${Date.now()}`,
        'popup=yes,width=1100,height=700',
      );
      return;
    }
    if (!selected.vpnIp) return;
    if (detail.id === 'copy-ssh') {
      const text = sshCommand(selected.vpnIp);
      void copyToClipboard(text).then((ok) => {
        if (ok) setFlash('SSH command copied to clipboard.');
        else setError('Could not copy the SSH command — the browser blocked clipboard access.');
      });
    } else if (detail.id === 'copy-portforward' && hubInstanceId) {
      const text = portForwardCommand(hubInstanceId, selected.vpnIp);
      void copyToClipboard(text).then((ok) => {
        if (ok) setFlash('SSM port-forward command copied to clipboard.');
        else setError('Could not copy the SSM port-forward command — the browser blocked clipboard access.');
      });
    } else if (detail.id === 'copy-webui-portforward' && hubInstanceId) {
      const text = webUiPortForwardCommand(hubInstanceId, selected.vpnIp);
      void copyToClipboard(text).then((ok) => {
        if (ok) setFlash('Web-UI port-forward command copied to clipboard.');
        else setError('Could not copy the web-UI port-forward command — the browser blocked clipboard access.');
      });
    }
  };

  // Link items only join the menu on an applied row (linkActionItems returns [] otherwise).
  const actionItems: ButtonDropdownProps.ItemOrGroup[] =
    state === 'notEnrolled'
      ? [{ id: 'initiate-this', text: 'Enroll this gateway' }]
      : [{ id: 're-initiate', text: 'Re-initiate…' }, ...linkActionItems(selected)];
  const actionsDisabledReason =
    state === 'inFlight' ? 'Enrollment in flight' : state === 'none' ? 'Select a gateway' : linkBusy ? 'Sending…' : undefined;

  const onActionItemClick = ({ detail }: { detail: ButtonDropdownProps.ItemClickDetails }) => {
    if (!selected) return;
    if (detail.id === 'initiate-this') {
      void onReinitiate('page');
      return;
    }
    if (detail.id === 're-initiate') {
      setReinitiateError(null);
      setReinitiateOpen(true);
      return;
    }
    const action = linkActionOfItem(detail.id);
    if (!action) return;
    if (detail.id === LINK_ITEM_IDS.down) {
      // Confirm first: taking the tunnel down interrupts an open terminal session.
      setDownError(null);
      setDownOpen(true);
    } else if (action === 'autostart') {
      void onLinkAction('autostart', autostartToggleTarget(selected.vpnAutostart));
    } else {
      void onLinkAction(action);
    }
  };

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description="Every provisioned gateway, enrolled or not: initiate asks the gateway for its WireGuard key, approve allocates its address and provisions the hub."
        >
          Gateway enrollment
        </Header>
      }
    >
      <SpaceBetween size="m">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)} header="Request failed">
            {error}
          </Alert>
        )}
        {flash && (
          <Alert type="success" dismissible onDismiss={() => setFlash(null)}>
            {flash}
          </Alert>
        )}
        {thingsUnavailable && (
          <Alert type="warning" header="Provisioned-gateway lookup unavailable">
            The IoT fleet index could not be read for {env}; only gateways with a VPN row are listed. Refresh to retry.
          </Alert>
        )}
        <Table<VpnGatewayRecord>
          {...collectionProps}
          items={visibleItems}
          loading={loading}
          loadingText="Loading VPN gateways…"
          trackBy={(g) => g.gatewayId}
          variant="container"
          selectionType="single"
          selectedItems={selected ? [selected] : []}
          onSelectionChange={({ detail }) => setSelected(detail.selectedItems[0] ?? null)}
          onRowClick={({ detail }) => setSelected(detail.item)}
          resizableColumns
          wrapLines={prefs.wrapLines}
          columnDisplay={prefs.contentDisplay}
          columnDefinitions={[
            {
              id: 'gatewayId',
              header: 'Gateway',
              sortingField: 'gatewayId',
              cell: (g) => {
                const href = `/vpn/${encodeURIComponent(g.gatewayId)}?env=${env}`;
                return (
                  <Box fontSize="body-s">
                    <Link href={href} onFollow={(e) => { e.preventDefault(); navigate(href); }}>
                      <code>{g.gatewayId}</code>
                    </Link>
                    {g.hostname && (
                      <Box fontSize="body-s" color="text-body-secondary">
                        {g.hostname}
                      </Box>
                    )}
                  </Box>
                );
              },
            },
            {
              id: 'vpnStatus',
              header: 'Status',
              sortingField: 'vpnStatus',
              cell: (g) => {
                const s = vpnStatusIndicator(g.vpnStatus);
                return <StatusIndicator type={s.type}>{s.label}</StatusIndicator>;
              },
            },
            {
              id: 'provisioned',
              header: 'Provisioned',
              sortingComparator: provisionedComparator,
              cell: (g) =>
                g.provisioning ? (
                  <StatusIndicator type={g.provisioning.status === 'COMPLETE' ? 'success' : 'pending'}>
                    {provisionedLabel(g.provisioning)}
                  </StatusIndicator>
                ) : (
                  '—'
                ),
            },
            {
              id: 'online',
              header: 'VPN online',
              cell: (g) => {
                // Only judge peers the hub actually has — a not-yet-provisioned gateway is "—",
                // not "offline".
                const peer = g.gatewayPubKey ? peerStatus?.byKey.get(g.gatewayPubKey) : undefined;
                if (!peerStatus || !peer) return '—';
                return peerOnline(peer, peerStatus.fetchedAt) ? (
                  <StatusIndicator type="success">online</StatusIndicator>
                ) : (
                  <StatusIndicator type="stopped">offline</StatusIndicator>
                );
              },
            },
            {
              id: 'link',
              header: 'Link',
              cell: (g) => {
                // What the gateway last REPORTED (vs. "VPN online", what the hub sees now).
                const link = linkIndicator(g);
                if (!link) return '—';
                return (
                  <span title={g.vpnLinkUpdatedAt ? `Reported ${g.vpnLinkUpdatedAt}` : undefined}>
                    <StatusIndicator type={link.type}>{link.label}</StatusIndicator>
                  </span>
                );
              },
            },
            { id: 'autostart', header: 'Autostart', cell: (g) => autostartLabel(g.vpnAutostart) },
            {
              id: 'lastSeen',
              header: 'Last seen',
              cell: (g) => {
                const peer = g.gatewayPubKey ? peerStatus?.byKey.get(g.gatewayPubKey) : undefined;
                if (!peer) return '—';
                return peer.latestHandshakeAt ? timeAgo(peer.latestHandshakeAt) : 'never';
              },
            },
            { id: 'vpnIp', header: 'VPN address', sortingField: 'vpnIp', cell: (g) => (g.vpnIp ? <code>{g.vpnIp}</code> : '—') },
            { id: 'groupId', header: 'Group', sortingField: 'groupId', cell: (g) => g.groupId ?? '—' },
            { id: 'customerProject', header: 'Customer / project', cell: (g) => customerProjectLabel(g.provisioning) },
            {
              id: 'gatewayPubKey',
              header: 'WireGuard key',
              sortingField: 'gatewayPubKey',
              cell: (g) => (g.gatewayPubKey ? <StatusIndicator type="success">reported</StatusIndicator> : '—'),
            },
            { id: 'updatedAt', header: 'Updated', sortingField: 'updatedAt', cell: (g) => g.updatedAt ?? '—' },
            { id: 'client', header: 'Client', sortingField: 'vpnClientVersion', cell: (g) => g.vpnClientVersion ?? '—' },
            {
              id: 'firmware',
              header: 'Firmware',
              sortingField: 'gatewayFirmwareVersion',
              cell: (g) => g.gatewayFirmwareVersion ?? '—',
            },
          ]}
          filter={
            <SpaceBetween direction="horizontal" size="s">
              <Select
                selectedOption={ENV_OPTIONS.find((o) => o.value === env) ?? ENV_OPTIONS[0]!}
                options={ENV_OPTIONS}
                ariaLabel="Environment"
                onChange={({ detail }) => {
                  setSelected(null);
                  setItems([]);
                  setPeerStatus(null);
                  setSearchParams({ env: detail.selectedOption.value ?? 'dev' });
                }}
              />
              <TextFilter
                {...filterProps}
                countText={`${filteredItemsCount} matches`}
                filteringPlaceholder="Find by gateway id, hostname, group, status, customer, project"
              />
            </SpaceBetween>
          }
          header={
            <Header
              counter={`(${items.length})`}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button iconName="status-info" onClick={() => setHelpOpen(true)}>
                    Connection help
                  </Button>
                  <Button
                    iconName="refresh"
                    ariaLabel="Refresh"
                    onClick={() => {
                      void load({ fresh: true });
                      void loadPeerStatus();
                    }}
                  />
                  <Button variant={state === 'requestedWithKey' ? 'normal' : 'primary'} onClick={openInitiate}>
                    Enroll gateway
                  </Button>
                  {state === 'requestedWithKey' && (
                    <Button
                      variant="primary"
                      onClick={() => {
                        setApproveError(null);
                        setApproveOpen(true);
                      }}
                    >
                      Approve…
                    </Button>
                  )}
                  {state === 'requestedNoKey' && (
                    <Button disabled disabledReason="Waiting for the gateway's WireGuard key">
                      Approve…
                    </Button>
                  )}
                  {state === 'failed' && (
                    <Button
                      onClick={() => {
                        setApproveError(null);
                        setApproveOpen(true);
                      }}
                    >
                      Approve…
                    </Button>
                  )}
                  <ButtonDropdown
                    items={connectItems}
                    disabled={connectDisabledReason !== undefined}
                    disabledReason={connectDisabledReason}
                    onItemClick={onConnectItemClick}
                  >
                    Connect
                  </ButtonDropdown>
                  <ButtonDropdown
                    items={actionItems}
                    disabled={actionsDisabledReason !== undefined}
                    disabledReason={actionsDisabledReason}
                    onItemClick={onActionItemClick}
                  >
                    Actions
                  </ButtonDropdown>
                </SpaceBetween>
              }
            >
              Gateways ({env})
            </Header>
          }
          pagination={<Pagination {...paginationProps} />}
          preferences={<TablePreferences prefs={prefs} onChange={setPrefs} contentOptions={CONTENT_OPTIONS} />}
          empty={
            <Box padding="m" textAlign="center">
              No gateways in {env}: nothing provisioned in the fleet index and no VPN rows.
            </Box>
          }
        />
      </SpaceBetween>
      <Modal
        visible={initiateOpen}
        onDismiss={() => setInitiateOpen(false)}
        header="Initiate VPN enrollment"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" disabled={initiating} onClick={() => setInitiateOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" loading={initiating} disabled={!initiateId.trim()} onClick={() => void onInitiate()}>
                Initiate
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {initiateError && (
            <Alert type="error" header="Initiate failed">
              {initiateError}
            </Alert>
          )}
          <FormField
            label="Gateway id"
            description="The gateway's uuid / ThingName (EUI-64 for LoRaWAN gateways). It may not be in the table yet."
          >
            <Input
              value={initiateId}
              onChange={({ detail }) => setInitiateId(detail.value)}
              placeholder="e.g. 647fdafffe0117df"
            />
          </FormField>
          <Box fontSize="body-s" color="text-body-secondary">
            Publishes the initiate command; the gateway generates its WireGuard keypair and reports the public key.
          </Box>
        </SpaceBetween>
      </Modal>
      <VpnConnectHelpModal
        visible={helpOpen}
        onDismiss={() => setHelpOpen(false)}
        vpnIp={selected?.vpnIp}
        hubInstanceId={hubInstanceId}
      />
      <VpnApproveModal
        visible={approveOpen}
        gateway={selected}
        submitting={approving}
        error={approveError}
        onDismiss={() => setApproveOpen(false)}
        onConfirm={(reason, dryRun) => void onApprove(reason, dryRun)}
      />
      <Modal
        visible={reinitiateOpen}
        onDismiss={() => setReinitiateOpen(false)}
        header="Re-initiate VPN enrollment"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" disabled={reinitiating} onClick={() => setReinitiateOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" loading={reinitiating} onClick={() => void onReinitiate()}>
                Re-initiate
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {reinitiateError && (
            <Alert type="error" header="Re-initiate failed">
              {reinitiateError}
            </Alert>
          )}
          <Alert type="warning">
            This rotates gateway <code>{selected?.gatewayId}</code>&apos;s WireGuard key. Any existing peer
            configuration on the hub becomes stale until the gateway reports its new key and is re-approved.
          </Alert>
        </SpaceBetween>
      </Modal>
      <Modal
        visible={downOpen}
        onDismiss={() => setDownOpen(false)}
        header="Take VPN down"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" disabled={linkBusy} onClick={() => setDownOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" loading={linkBusy} onClick={() => void onLinkAction('down', undefined, 'modal')}>
                Take VPN down
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {downError && (
            <Alert type="error" header="Take VPN down failed">
              {downError}
            </Alert>
          )}
          <Alert type="warning">
            This takes gateway <code>{selected?.gatewayId}</code>&apos;s tunnel down and interrupts any open terminal
            session to it. The gateway stays down until you bring the VPN up again or it reboots with autostart on.
          </Alert>
        </SpaceBetween>
      </Modal>
    </ContentLayout>
  );
}
